import type { ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { useCurrentUserQuery } from '@/api/user';
import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { FlexiForwardFormField } from '@/App/tiles/FlexiForward/FlexiForwardTile/common/form/FlexiForwardFormField.tsx';
import { FlexiForwardSwitch } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/FlexiForwardSwitch.tsx';
import { useFlexiForwardPropertyChangedHandler } from '@/store/flexi-forward/hooks/useFlexiForwardPropertyChangedHandler.ts';
import { type ClientsWay, DEFAULT_CLIENTS_WAY, POSSIBLE_CLIENTS_WAY } from '@/store/flexi-forward/state.ts';

export function ClientsWaySwitch({
  tileId,
  currency,
  notionalCurrencyIndex,
}: {
  tileId: string;
  currency: string;
  notionalCurrencyIndex: number;
}): ReactElement {
  const { formatMessage } = useIntl();

  const { data: user } = useCurrentUserQuery();

  const selectedClientsWay = useFlexiForwardSelector(
    (flexiForward) => flexiForward.properties.clientsWay ?? DEFAULT_CLIENTS_WAY,
  );

  const handleClientsWayChanged = useFlexiForwardPropertyChangedHandler<ClientsWay>(
    tileId,
    "Client's way change",
    (clientsWay) => ({ clientsWay }),
  );

  const formattedMessageValues = { isInternalSales: user?.userType === 'Internal Sales' };

  function formatClientsWay(value: ClientsWay): string {
    const way = notionalCurrencyIndex === 2 ? (value === 'Buy' ? 'Sell' : 'Buy') : value;

    return formatMessage(
      {
        id: `flexiForwardTile.mainFields.clientsWay.toggle.${way}`,
      },
      { currency },
    );
  }

  return (
    <FlexiForwardFormField
      formattedMessageId="flexiForwardTile.mainFields.clientsWay.label"
      formattedMessageValues={formattedMessageValues}
    >
      <FlexiForwardSwitch<ClientsWay>
        name="client-way"
        identifier={tileId}
        values={POSSIBLE_CLIENTS_WAY}
        selectedValue={selectedClientsWay}
        onChange={handleClientsWayChanged}
        formatValue={formatClientsWay}
      />
    </FlexiForwardFormField>
  );
}
